import { css } from 'aphrodite';
import { useState } from 'react';
import { THEME } from '../../../../../../../components-v2/Theme/theme';
import { Button } from '../../../../../../../components/Button';
import { InlineValueEditor } from '../../../../../../../components/entities/InlineValueEditor';
import { EditItemPenIcon } from '../../../../../../../components/svgs/icons/EditItemPenIcon';
import { TrashIcon } from '../../../../../../../components/svgs/icons/TrashIcon';
import { WebSiteIcon } from '../../../../../../../components/svgs/icons/WebsiteIcon';
import { DeleteConfirmationPopover } from '../DeleteConfirmationPopover';
import { styleSheet } from '../../styles';

interface WebsiteCardProps {
	website: string;
	onWebsiteChanged: (website: string) => void;
	onDelete: () => void;
}

export const WebsiteCard = ({ website, onWebsiteChanged, onDelete }: WebsiteCardProps) => {
	const [isEditing, setIsEditing] = useState(false);
	const [websiteValue, setWebsiteValue] = useState(website);
	const [isDeleting, setIsDeleting] = useState(false);

	const updateWebsite = () => {
		setIsEditing(false);
		onWebsiteChanged(websiteValue);
	};

	const formatWebsiteUrl = (url: string) => {
		if (!url) return '';
		// Add protocol if missing
		if (!url.startsWith('http://') && !url.startsWith('https://')) {
			return `https://${url}`;
		}
		return url;
	};

	const displayWebsite = (url: string) => {
		if (!url) return '';
		// Remove protocol for display
		return url.replace(/^https?:\/\//, '');
	};

	if (!website) {
		return null;
	}

	if (isEditing) {
		return (
			<InlineValueEditor
				autoComplete='off'
				autoFocus={true}
				compactLayout={true}
				id='website-editor-input'
				onCancelButtonClicked={() => setIsEditing(false)}
				onChange={ev => setWebsiteValue(ev.target.value)}
				onKeyDown={ev => {
					if (ev.keyCode === 13) {
						updateWebsite();
					}
				}}
				onSaveButtonClicked={updateWebsite}
				value={websiteValue || ''}
				placeholder='Enter website URL'
			/>
		);
	}

	return (
		<div className={css(styleSheet.emailCard, styleSheet.websiteCard)}>
			<div className={css(styleSheet.emailCardIcon)}>
				<WebSiteIcon fill={THEME.colors.border.main} height={15} width={15} />
			</div>
			<div className={css(styleSheet.emailCardContent)}>
				<div className={css(styleSheet.emailCardContentRow, styleSheet.websiteCardContentRow)}>
					<a
						className={css(styleSheet.emailCardValue)}
						href={formatWebsiteUrl(website)}
						target='_blank'
						rel='noopener noreferrer'
					>
						{displayWebsite(website)}
					</a>
					<div className={`${css(styleSheet.emailCardActions)} actions ${isDeleting ? 'visible' : null}`}>
						<Button kind='icon' onClick={() => setIsEditing(true)}>
							<EditItemPenIcon />
						</Button>

						<DeleteConfirmationPopover
							open={isDeleting}
							onOpenChange={setIsDeleting}
							onConfirm={onDelete}
							message={<>Do you want to delete the website {displayWebsite(website)}?</>}
						>
							<Button kind='icon' onClick={() => setIsDeleting(true)}>
								<TrashIcon />
							</Button>
						</DeleteConfirmationPopover>
					</div>
				</div>
			</div>
		</div>
	);
};
